import { ColdTransferToQueue20Regular } from "@/common/icons";
import { useMessage } from "@/common/messages";
import {
	AppCacheState,
	AppSettingsState,
	CacheKey,
	useAppCacheStore,
} from "@/common/stores/persistingStores/app";
import {
	CurrentInteractionState,
	useCurrentInteractionStore,
} from "@/common/stores/nonpersistingStores/currentInteraction";
import {
	CallSessionState,
	useCallSessionStore,
} from "@/common/stores/nonpersistingStores/callSession";
import { AgentInteractionStatus } from "@/common/constants/callSession";
import { InteractionPopoverType, InteractionType } from "@/common/types";
import { MessagingConversationSummaryButton } from "@/components/atoms/messagingConversationSummary";
import { Flex } from "@/components/atoms/flex";
import { NotesButton } from "@/components/atoms/notes";
import { PopoverView } from "@/components/atoms/popover";
import { SquareToggleButton } from "@/components/atoms/squareToggleButton";
import { InteractionTransferToAgentPopover } from "@/components/organisms/InteractionTransferToAgent";
import { InteractionTransferIvrPopover } from "@/components/organisms/interactionTransferToIvr/InteractionTransferIvrPopover";
import { useTextMonitoring } from "@/hooks/supervisor";
import { pauseInteraction, resumeInteraction } from "@/repository/queryFns";
import { mergeClasses, tokens } from "@fluentui/react-components";
import {
	ArrowForward20Regular,
	Dismiss20Regular,
	HourglassHalf20Regular,
	Play20Regular,
	TextAddSpaceBefore20Filled,
	TextHanging20Regular,
} from "@fluentui/react-icons";
import { useMutation } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { MessagingInteractionInProgressProps } from "./types";
import { addBreadcrumb, captureError } from "@/common/helpers/sentryHelper";
import { useInteractionBarCommonStyle } from "@/common/style/interactionBarCommonStyle";
import {
	QueuesState,
	useQueuesStore,
} from "@/common/stores/nonpersistingStores/queues";
import { useWebchatCallRequestStore } from "@/common/stores/nonpersistingStores/webchatCallRequest";
import { InteractionDirection } from "@/repository/models";
import { useAppSettingsStore } from "@/common/stores/persistingStores/app/appSettingsStore";
import { useEmailDraftSavingStore } from "@/common/stores/nonpersistingStores/emailDraftSaving";
import { parkTextInteraction as parkTextInteractionHelper } from "@/common/helpers/parkHelper";
import { SUBJECT_CHANGE_INFO_MESSAGE_KEY } from "@/common/constants";

const resolveTransferTooltipId = (
	isVideoCallInProgress: boolean,
	isTransferAllowed: boolean,
	enabledId: string,
	disabledId: string
): string => {
	if (isVideoCallInProgress) {
		return "Interaction.Buttons.TransferDisabledDuringVideoCall";
	}

	return isTransferAllowed ? enabledId : disabledId;
};

export const MessagingInProgressActionButtons = (
	props: MessagingInteractionInProgressProps
) => {
	const {
		transferChannelType,
		transferType,
		transferIVRType,
		closeInteraction,
		enablePauseButton = false,
		additionalActions,
	} = props;
	const style = useInteractionBarCommonStyle();
	const intl = useIntl();
	const appStoreCache = useAppCacheStore((state: AppCacheState) => state.cache);
	const [isPauseButtonVisible, setIsPauseButtonVisible] =
		useState<boolean>(false);
	const [pauseActionInProgress, setPauseActionInProgress] =
		useState<boolean>(false);
	const { show: showMessage, hide: hideMessage } = useMessage();
	const [interactionPopoverType, setInteractionPopoverType] =
		useState<InteractionPopoverType | null>(null);
	const queues = useQueuesStore((state: QueuesState) => state.queues);

	const currentInteraction = useCurrentInteractionStore(
		(state: CurrentInteractionState) => state.active
	);

	const callSession = useCallSessionStore(
		(state: CallSessionState) => state.callSession
	);

	const pendingVideoCallRequest = useWebchatCallRequestStore((state) =>
		currentInteraction
			? state.pendingByConversationId[currentInteraction.id]
			: undefined
	);

	// The video call is this agent's own call session, so we match it by conversation id.
	const isWebchatVideoCallActive =
		!!callSession &&
		!!currentInteraction &&
		callSession.webchatConversationId === currentInteraction.id &&
		callSession.state !== AgentInteractionStatus.Terminated;

	// An agent-initiated webchat video call pins its interaction in place — it can be neither
	// transferred nor parked — for the WHOLE call, starting the moment the agent hits Start rather
	// than once the call is up: moving or parking the interaction while the invite is out strands
	// the customer's side of a call that is already on its way. The pending request covers the
	// invite and the ring (it is dropped once the call goes active), the call session covers the
	// live call.
	const isWebchatVideoCallInProgress =
		!!pendingVideoCallRequest || isWebchatVideoCallActive;

	const enableSummary = queues.find(q => q.id === currentInteraction?.queueId)?.enableTextSummary ?? false;

	const isTextMonitoring = useTextMonitoring();
	const appSettings = useAppSettingsStore(
		(state: AppSettingsState) => state.settings
	);

	const { cancel: cancelDraftSave, markAsClosing } = useEmailDraftSavingStore();
	const closeMessagingInteractionMutation = useMutation({
		mutationFn: closeInteraction,
		onError: (err: any, data: any, _context: any) => {
			captureError(err, "Error closing messaging interaction", undefined, data);
			showMessage("workspace", {
				intent: "error",
				text: intl.formatMessage({ id: "Interaction.Messages.ErrorOnClosing" }),
			});
		},
	});

	useEffect(() => {
		setPauseActionInProgress(false);

		setIsPauseButtonVisible(currentInteraction?.isOnHold ? false : true);
	}, [currentInteraction]);

	const handleMessagingClosing = useCallback(() => {
		const currentInteractionId = appStoreCache[CacheKey.CurrentInteractionId];

		if (
			currentInteraction?.type == InteractionType.Email &&
			currentInteraction?.id
		) {
			hideMessage("historyWorkspace", SUBJECT_CHANGE_INFO_MESSAGE_KEY);
			hideMessage("workspace", SUBJECT_CHANGE_INFO_MESSAGE_KEY);
			markAsClosing(currentInteraction.id);
			cancelDraftSave(currentInteraction.id);
		}
		addBreadcrumb("ui_interaction", "Closing messaging interaction", {
			currentInteractionId,
		});
		closeMessagingInteractionMutation.mutate(currentInteractionId);
	}, [closeMessagingInteractionMutation]);

	const toggleInteractionPopoverOpening = useCallback(
		(type: InteractionPopoverType) => {
			setInteractionPopoverType((oldState) =>
				oldState === type ? null : type
			);
		},
		[setInteractionPopoverType]
	);

	const getPauseButtonByStatus = useCallback(
		(isPause: boolean) => {
			const buttonIcon = isPause ? (
				<TextAddSpaceBefore20Filled />
			) : (
				<Play20Regular />
			);
			const state = isPause ? "PersonalPark" : "Resume";
			const currentInteractionId = appStoreCache[CacheKey.CurrentInteractionId];

			return (
				<SquareToggleButton
					icon={pauseActionInProgress ? <HourglassHalf20Regular /> : buttonIcon}
					tooltip={intl.formatMessage({
						id: isWebchatVideoCallInProgress
							? "Interaction.Buttons.ParkDisabledDuringVideoCall"
							: `Interaction.Buttons.${state}`,
					})}
					onClick={() => {
						setPauseActionInProgress(true);

						isPauseButtonVisible
							? pauseInteraction(currentInteractionId)
							: resumeInteraction(currentInteractionId);
					}}
					disabled={pauseActionInProgress || isWebchatVideoCallInProgress}
				/>
			);
		},
		[
			isPauseButtonVisible,
			currentInteraction,
			pauseActionInProgress,
			isWebchatVideoCallInProgress,
		]
	);

	const parkTextInteraction = useCallback(() => {
		const currentInteractionId = appStoreCache[CacheKey.CurrentInteractionId];

		parkTextInteractionHelper(currentInteractionId, currentInteraction?.type);
	}, [currentInteraction, appStoreCache]);

	const parkButton = useCallback(() => {
		const agentHasParkingRightOnQueue = queues.find(
			(queueUser: any) =>
				queueUser.id === currentInteraction?.queueId &&
				queueUser.isOperator &&
				queueUser.hasParkingRight
		);

		return agentHasParkingRightOnQueue &&
			!isTextMonitoring &&
			!currentInteraction?.wasTransferred &&
			currentInteraction?.type !== InteractionType.Generic &&
			currentInteraction?.direction === InteractionDirection.Incoming ? (
			<SquareToggleButton
				icon={<TextHanging20Regular />}
				tooltip={intl.formatMessage({
					id: isWebchatVideoCallInProgress
						? "Interaction.Buttons.ParkDisabledDuringVideoCall"
						: "Interaction.Buttons.ParkOnQueue",
				})}
				onClick={() => {
					parkTextInteraction();
				}}
				disabled={isWebchatVideoCallInProgress}
			/>
		) : undefined;
	}, [currentInteraction, queues, isWebchatVideoCallInProgress]);

	const isTransferEnabled = appSettings.messagingColdTransferEnabled;
	const isTransferIVREnabled = appSettings.messagingColdTransferIVREnabled;

	return (
		<Flex
			vAlign="center"
			className={mergeClasses(style.interactionText, style.interactionButtons)}
		>
			{additionalActions}
			{enablePauseButton &&
				!isTextMonitoring &&
				getPauseButtonByStatus(isPauseButtonVisible)}
			{parkButton()}

			<PopoverView
				trigger={
					<SquareToggleButton
						icon={<ArrowForward20Regular />}
						tooltip={intl.formatMessage({
							id: resolveTransferTooltipId(
								isWebchatVideoCallInProgress,
								isTransferEnabled,
								"Interaction.Buttons.ColdTransfer",
								"Interaction.Buttons.ColdTransferDisabled"
							),
						})}
						onClick={() => toggleInteractionPopoverOpening(transferType)}
						disabled={!isTransferEnabled || isWebchatVideoCallInProgress}
					/>
				}
				content={
					<InteractionTransferToAgentPopover
						closePopover={() => toggleInteractionPopoverOpening(transferType)}
						channelType={transferChannelType}
					/>
				}
				positioning={{ position: "below" }}
				open={interactionPopoverType === transferType}
			/>
			<PopoverView
				trigger={
					<SquareToggleButton
						icon={<ColdTransferToQueue20Regular />}
						tooltip={intl.formatMessage({
							id: resolveTransferTooltipId(
								isWebchatVideoCallInProgress,
								isTransferIVREnabled,
								"Interaction.Buttons.ColdTransferToOnlyQueue",
								"Interaction.Buttons.QueueColdTransferDisabled"
							),
						})}
						onClick={() => toggleInteractionPopoverOpening(transferIVRType)}
						disabled={!isTransferIVREnabled || isWebchatVideoCallInProgress}
					/>
				}
				content={
					<InteractionTransferIvrPopover
						closePopover={() =>
							toggleInteractionPopoverOpening(transferIVRType)
						}
						channelType={transferChannelType}
					/>
				}
				positioning={{ position: "below" }}
				open={interactionPopoverType === transferIVRType}
			/>

			<NotesButton
				interactionId={currentInteraction?.id!}
				editMode={true}
				toggleInteractionPopoverOpening={toggleInteractionPopoverOpening}
				interactionPopoverType={interactionPopoverType}
			/>
			{enableSummary && currentInteraction && (
				<MessagingConversationSummaryButton
					canGenerateSummary={enableSummary}
					summaryFeatureEnabled={enableSummary}
					interactionId={currentInteraction.id}
					interactionType={currentInteraction.type}
					interactionStartDate={currentInteraction.startDate?.toString()}
					firstAgentAssignedAt={currentInteraction.firstAgentAssignedAt}
				/>
			)}
			<SquareToggleButton
				icon={
					<Dismiss20Regular
						primaryFill={tokens.colorNeutralForegroundStaticInverted}
					/>
				}
				tooltip={intl.formatMessage({ id: "Interaction.Buttons.Close" })}
				className={mergeClasses(style.squareColoredButton, style.redColor)}
				onClick={handleMessagingClosing}
				disabled={closeMessagingInteractionMutation.isPending}
			/>
		</Flex>
	);
};
